import React, { useState, useEffect, useContext } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { SupabaseService } from '../../services/supabaseService'
import LinkDirectoryTable from './LinkDirectoryTable'
import LinkDirectoryFilters from './LinkDirectoryFilters'
import UnlockModal from './UnlockModal'
import ReportBrokenLinkModal from './ReportBrokenLinkModal'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { ThemeContext } from '../../App'
import RequestLinksModal from './RequestLinksModal'
import RequestLogsModal from './RequestLogsModal'

const { FiDatabase, FiLock, FiArrowLeft, FiSun, FiMoon, FiCheck, FiSave, FiAlertCircle, FiInfo, FiLink } = FiIcons

const MAX_PROMPT_WORDS = 225

const LinkDirectory = ({ user, userCredits, onPurchaseClick, onBack, initialFilters }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useContext(ThemeContext)
  const [hasAccess, setHasAccess] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [opportunities, setOpportunities] = useState([])
  const [filteredOpportunities, setFilteredOpportunities] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState(initialFilters || { niche: '', type: '', state: '', search: '', favorites: false })
  const [uniqueNiches, setUniqueNiches] = useState([])
  const [uniqueTypes, setUniqueTypes] = useState([])
  const [uniqueStates, setUniqueStates] = useState([])
  const [unlocking, setUnlocking] = useState(false)
  const [error, setError] = useState('')
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [reportedOpportunity, setReportedOpportunity] = useState(null)
  const [userActions, setUserActions] = useState({})
  const [totalOpportunities, setTotalOpportunities] = useState(0)
  const [personalizationData, setPersonalizationData] = useState({ default_name: '', default_title: '', default_company: '', default_email: '', default_website_url: '', default_custom_prompt: '' })
  const [defaultValues, setDefaultValues] = useState({})
  const [isSavingPersonalization, setIsSavingPersonalization] = useState(false)
  const [personalizationSuccess, setPersonalizationSuccess] = useState(false)
  const [personalizationWarning, setPersonalizationWarning] = useState(false)
  const [showPersonalizationSection, setShowPersonalizationSection] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [showPromptTooltip, setShowPromptTooltip] = useState(false)
  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState('success')
  const [promptTooLong, setPromptTooLong] = useState(false)
  const [requestLogsOpen, setRequestLogsOpen] = useState(false)
  const [requestLogs, setRequestLogs] = useState([])
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState(false)

  const itemsPerPage = 50

  useEffect(() => {
    checkAccess()
  }, [user])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    let toastTimer
    if (toastVisible) {
      toastTimer = setTimeout(() => setToastVisible(false), 5000)
    }
    return () => {
      if (toastTimer) clearTimeout(toastTimer)
    }
  }, [toastVisible])

  useEffect(() => {
    if (hasAccess) {
      loadOpportunities()
      ;(async () => {
        // Load personalization and defaults together after mount
        await loadPersonalizationData()
        const userData = await SupabaseService.getUserPersonalization(user.id, null)
        setDefaultValues({ webhook_url: userData.webhook_url || '' })
      })()
    }
  }, [hasAccess])

  // Track default_custom_prompt word count and over-limit state
  useEffect(() => {
    const text = (personalizationData.default_custom_prompt || '').trim()
    const count = text === '' ? 0 : text.split(/\s+/).filter(Boolean).length
    setPromptTooLong(count > MAX_PROMPT_WORDS)
  }, [personalizationData.default_custom_prompt])

  useEffect(() => {
    if (opportunities.length > 0) {
      applyFilters()
    }
  }, [opportunities, filters, currentPage])

  useEffect(() => {
    if (hasAccess && user) {
      loadUserActions()
      loadRequestLogs()
    }
  }, [hasAccess, user])

  const checkAccess = async () => {
    try {
      setLoading(true)
      const access = await SupabaseService.checkDirectoryAccess(user.id)
      setHasAccess(access)
      if (!access) {
        setShowUnlockModal(true)
      }
    } catch (error) {
      console.error('Error checking access:', error)
      setError('Failed to check access status')
    } finally {
      setLoading(false)
    }
  }

  const loadOpportunities = async () => {
    try {
      const data = await SupabaseService.getLinkDirectoryOpportunities()
      setOpportunities(data)
      setTotalOpportunities(data.length)
      // Extract unique niches, types, and states for filters
      const niches = [...new Set(data.map(item => item.niche))].sort()
      const types = [...new Set(data.map(item => item.type))].sort()
      const states = [...new Set(data.map(item => item.state))].filter(Boolean).sort()
      
      setUniqueNiches(niches)
      setUniqueTypes(types)
      setUniqueStates(states)
    } catch (error) {
      console.error('Error loading opportunities:', error)
      setError('Failed to load opportunities')
    }
  }

  const loadUserActions = async () => {
    try {
      const actions = await SupabaseService.getUserLinkDirectoryActions(user.id)
      const actionsMap = {}
      actions.forEach(action => {
        actionsMap[action.link_id] = { favorited: action.favorited, status: action.status, is_open: action.is_open }
      })
      setUserActions(actionsMap)
    } catch (error) {
      console.error('Error loading user actions:', error)
    }
  }
  
  const loadPersonalizationData = async () => {
    try {
      const userData = await SupabaseService.getUserPersonalization(user.id, null);
      
      setPersonalizationData({
        default_name: userData.name || '',
        default_title: userData.title || '',
        default_company: userData.company || '',
        default_email: userData.email || '',
        default_website_url: userData.website || '',
        default_custom_prompt: ''
      });

      // Also load Link Directory–specific default_custom_prompt from user_outreach_defaults_k9l8x3
      const { data: defaultsRow } = await SupabaseService.supabase
        .from('user_outreach_defaults_k9l8x3')
        .select('default_custom_prompt')
        .eq('user_id', user.id)
        .maybeSingle()
      setPersonalizationData(prev => ({
        ...prev,
        default_custom_prompt: (defaultsRow?.default_custom_prompt || '')
      }))
      
      // Check if any fields are empty
      const hasEmptyField = Object.values({
        default_name: userData.name || '',
        default_title: userData.title || '',
        default_company: userData.company || '',
        default_email: userData.email || '',
        default_website_url: userData.website || ''
      }).some(value => !value);
      
      setPersonalizationWarning(hasEmptyField);
    } catch (error) {
      console.error('Failed to load personalization data:', error);
    }
  };
  
  const savePersonalizationData = async () => {
    // Enforce 225-word limit on default_custom_prompt
    const text = (personalizationData.default_custom_prompt || '').trim()
    const count = text === '' ? 0 : text.split(/\s+/).filter(Boolean).length
    if (count > MAX_PROMPT_WORDS) {
      setToastType('error')
      setToastMessage(`Custom prompt is limited to ${MAX_PROMPT_WORDS} words. Please shorten it to save.`)
      setToastVisible(true)
      return
    }
    setIsSavingPersonalization(true);
    try {
      // Update user_outreach_defaults_k9l8x3 table
      const { data: existingData } = await SupabaseService.supabase
        .from('user_outreach_defaults_k9l8x3')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (existingData) {
        // Update existing record
        const { error } = await SupabaseService.supabase
          .from('user_outreach_defaults_k9l8x3')
          .update({
            default_name: personalizationData.default_name,
            default_title: personalizationData.default_title,
            default_company: personalizationData.default_company,
            default_email: personalizationData.default_email,
            default_website_url: personalizationData.default_website_url,
            default_custom_prompt: personalizationData.default_custom_prompt,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await SupabaseService.supabase
          .from('user_outreach_defaults_k9l8x3')
          .insert([{
            user_id: user.id,
            default_name: personalizationData.default_name,
            default_title: personalizationData.default_title,
            default_company: personalizationData.default_company,
            default_email: personalizationData.default_email,
            default_website_url: personalizationData.default_website_url,
            default_custom_prompt: personalizationData.default_custom_prompt,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
        
        if (error) throw error;
      }

      // Show success message and clear after 3 seconds
      setPersonalizationSuccess(true);
      setTimeout(() => {
        setPersonalizationSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving personalization data:', error);
    } finally {
      setIsSavingPersonalization(false);
    }
  };
  
  const handlePersonalizationChange = (e) => {
    const { name, value } = e.target;
    setPersonalizationData(prev => ({ ...prev, [name]: value }));

    // Clear success message when form is modified
    if (personalizationSuccess) {
      setPersonalizationSuccess(false);
    }

    // Check if any field is empty after change
    checkPersonalizationFields({ ...personalizationData, [name]: value });
  };
  
  const checkPersonalizationFields = (data = null) => {
    const fields = data || personalizationData;
    const { default_name, default_title, default_company, default_email, default_website_url } = fields;
    const hasEmptyField = [default_name, default_title, default_company, default_email, default_website_url].some(value => !value);
    setPersonalizationWarning(hasEmptyField);

    // If there's an empty field, make sure the section is open
    if (hasEmptyField) {
      setShowPersonalizationSection(true);
    }
  };

  const applyFilters = () => {
    let filtered = [...opportunities]

    // Apply state filter
    if (filters.state) {
      filtered = filtered.filter(item => item.state === filters.state)
    }

    // Apply niche filter
    if (filters.niche) {
      filtered = filtered.filter(item => item.niche === filters.niche)
    }

    // Apply type filter
    if (filters.type) {
      filtered = filtered.filter(item => item.type === filters.type)
    }

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filtered = filtered.filter(item =>
        item.link_opportunity.toLowerCase().includes(searchTerm) ||
        item.niche.toLowerCase().includes(searchTerm) ||
        item.type.toLowerCase().includes(searchTerm) ||
        (item.contact && item.contact.toLowerCase().includes(searchTerm)) ||
        (item.state && item.state.toLowerCase().includes(searchTerm))
      )
    }

    // Apply favorites filter
    if (filters.favorites) {
      filtered = filtered.filter(item => userActions[item.id]?.favorited === true)
    }

    // Set total filtered count
    setTotalOpportunities(filtered.length)

    // Calculate pagination
    const pages = Math.ceil(filtered.length / itemsPerPage)
    setTotalPages(pages)

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = filtered.slice(startIndex, endIndex)
    setFilteredOpportunities(paginatedData)
  }

  const handleUnlock = async () => {
    // No longer spending credits or manually granting access here.
    // Access will be granted automatically via webhook after subscription checkout.
    setError('')
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleReportBrokenLink = (opportunity) => {
    setReportedOpportunity(opportunity)
    setReportModalOpen(true)
  }

  const handleFavorite = (opportunity) => {
    const isFavorited = userActions[opportunity.id]?.favorited || false
    if (isFavorited) {
      // Handle unfavoriting
      handleSaveFavorite(opportunity, false)
      return
    }

    // Just save the favorite without showing modal
    handleSaveFavorite(opportunity, true)
  }

  const handleSaveFavorite = async (opportunity, favorited) => {
    try {
      await SupabaseService.saveLinkDirectoryAction(
        user.id,
        opportunity.id,
        favorited, // favorited status - can be true or false
        userActions[opportunity.id]?.status || opportunity.status || 'New'
      )

      // Update local state
      setUserActions(prev => ({
        ...prev,
        [opportunity.id]: { ...prev[opportunity.id], favorited: favorited }
      }))
    } catch (error) {
      console.error('Error saving favorite:', error)
    }
  }

  const handleStatusChange = async (opportunityId, newStatus) => {
    try {
      await SupabaseService.saveLinkDirectoryAction(
        user.id,
        opportunityId,
        userActions[opportunityId]?.favorited || false,
        newStatus
      )

      // Update local state
      setUserActions(prev => ({
        ...prev,
        [opportunityId]: { ...prev[opportunityId], status: newStatus }
      }))

      // Also update opportunity status
      await SupabaseService.updateDirectoryOpportunityStatus(opportunityId, newStatus)
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const handleReportSubmit = async (confirmed) => {
    if (confirmed && reportedOpportunity) {
      try {
        await SupabaseService.reportBrokenLink(
          user.id,
          reportedOpportunity.id,
          reportedOpportunity.link_opportunity
        )

        // Close modal after successful report
        setReportModalOpen(false)
        setReportedOpportunity(null)
      } catch (error) {
        console.error('Error reporting broken link:', error)
        // Keep modal open with error message
      }
    } else {
      // Just close the modal if not confirmed
      setReportModalOpen(false)
      setReportedOpportunity(null)
    }
  }
  
  const handleNavigateToSettings = () => {
    window.location.href = '/#/settings';
  };

  const handleRequestLinksSubmit = async ({ domain, niche, keyword, linkTypes, email }) => {
    try {
      const subject = `Link Request: ${domain}`
      const message = `Domain: ${domain}\nNiche/Industry: ${niche}\nPrimary Keyword: ${keyword}\nTypes of Links: ${linkTypes}\nEmail Address: ${email}`

      const response = await fetch('https://webhooks.integrately.com/a/webhooks/7f025e5cc805486285705d5c608214dc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'support@k9links.com', subject, message })
      })

      if (!response.ok) throw new Error('Failed to send request')

      const sentAtIso = new Date().toISOString()
      await SupabaseService.logSentEmail(user.id, null, null, email, subject, sentAtIso)

      setRequestModalOpen(false)
      setToastMessage('Your request has been sent to our team successfully.')
      setToastType('success')
      setToastVisible(true)
      setHasSubmittedRequest(true)
      await loadRequestLogs()
      return true
    } catch (e) {
      console.error('Failed to submit link request:', e)
      return false
    }
  }

  const loadRequestLogs = async () => {
    try {
      const { data, error } = await SupabaseService.supabase
        .from('email_logs')
        .select('subject, sent_at')
        .eq('user_id', user.id)
        .is('search_id', null)
        .is('result_id', null)
        .is('link_id', null)
        .order('sent_at', { ascending: false })
      if (error) throw error
      setRequestLogs(data || [])
    } catch (e) {
      console.error('Failed to load request logs:', e)
      setRequestLogs([])
    }
  }

  const exportToCSV = () => {
    // Determine which data toexport based on filters
    const dataToExport = Object.keys(filters).some(key => filters[key] ? true : false)
      ? filteredOpportunities
      : opportunities

    // Create CSV headers
    const headers = ['State', 'Niche', 'Type', 'Link Opportunity', 'Contact', 'Status']

    // Create CSV rows
    const csvRows = []
    csvRows.push(headers.join(','))

    // Add data rows
    dataToExport.forEach(item => {
      const state = `"${(item.state_code || '').replace(/"/g, '""')}"`
      const niche = `"${(item.niche || '').replace(/"/g, '""')}"`
      const type = `"${(item.type || '').replace(/"/g, '""')}"`
      const linkOpp = `"${(item.link_opportunity || '').replace(/"/g, '""')}"`
      const contact = item.contact ? `"${item.contact.replace(/"/g, '""')}"` : '""'
      const status = userActions[item.id]?.status || item.status || 'New'

      csvRows.push([state, niche, type, linkOpp, contact, status].join(','))
    })

    // Create CSV content
    const csvContent = csvRows.join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `link_directory_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const sendEmailsViaWebhook = async (emails, subject, message) => {
    const webhookUrl = (defaultValues.webhook_url || '').trim()
    for (const email of emails) {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, subject, message })
      })
      if (!response.ok) throw new Error('Failed to send email')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D59A4C]"></div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UnlockModal
          show={showUnlockModal}
          onBack={onBack}
          error={error}
          user={user}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-y-3">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors mr-4"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
                Back
              </button>
              <div className="flex items-center">
                <div className="bg-[#D59A4C20] p-3 rounded-lg mr-3">
                  <SafeIcon icon={FiDatabase} className="w-6 h-6 text-[#D59A4C]" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Link Directory</h1>
                  <p className="hidden sm:block text-gray-600 dark:text-gray-400">
                    {opportunities.length} curated link building opportunities
                  </p>
                </div>
              </div>
            </div>
            {/* Desktop actions (all inline) */}
            <div className="hidden sm:flex items-center space-x-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors no-border"
              >
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-5 h-5" />
              </button>
              {requestLogs.length > 0 && (
                <button
                  onClick={async () => { await loadRequestLogs(); setRequestLogsOpen(true) }}
                  className="px-3 py-2 text-[#D59A4C] hover:text-[#B58C54] rounded-lg transition-colors flex items-center no-border"
                >
                  <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2" />
                  View Requests
                </button>
              )}
              <button
                onClick={() => setRequestModalOpen(true)}
                className="px-3 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors flex items-center"
              >
                <SafeIcon icon={FiLink} className="w-4 h-4 mr-2" />
                Request Links
              </button>
            </div>

            {/* Mobile: keep toggle on first row, move buttons below */}
            <div className="flex sm:hidden items-center">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors no-border"
              >
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-5 h-5" />
              </button>
            </div>
            <div className="w-full sm:hidden flex items-center space-x-3 justify-start">
              {requestLogs.length > 0 && (
                <button
                  onClick={async () => { await loadRequestLogs(); setRequestLogsOpen(true) }}
                  className="px-3 py-2 text-[#D59A4C] hover:text-[#B58C54] rounded-lg transition-colors flex items-center no-border"
                >
                  <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2" />
                  View Requests
                </button>
              )}
              <button
                onClick={() => setRequestModalOpen(true)}
                className="px-3 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors flex items-center"
              >
                <SafeIcon icon={FiLink} className="w-4 h-4 mr-2" />
                Request Links
              </button>
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 dark:bg-red-900/30 dark:border-red-600">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Toast notification */}
        {toastVisible && (
          <motion.div
            initial={{opacity: 0, y: -50}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -50}}
            className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex items-center ${
              toastType === 'success' ? 'bg-white dark:bg-gray-800' : 'bg-red-50 dark:bg-red-900'
            } p-4 rounded-lg shadow-lg max-w-sm`}
          >
            <img src="https://i.imghippo.com/files/GkAk1182MIE.webp" alt="K9 Links Dog" className="w-12 h-12 mr-3" />
            <p className={`${toastType === 'success' ? 'text-gray-800 dark:text-white' : 'text-red-600 dark:text-red-400'} font-medium`}>{toastMessage}</p>
          </motion.div>
        )}
        
        {/* Outreach Personalization Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Outreach Personalization</h3>
            <div className="flex items-center">
              {personalizationSuccess && (
                <div className="text-green-600 flex items-center mr-3 dark:text-green-400">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" />
                  <span>Saved successfully</span>
                </div>
              )}
              <button
                onClick={() => setShowPersonalizationSection(!showPersonalizationSection)}
                className="text-[#D59A4C] hover:text-[#B58C54] font-medium transition-colors flex items-center bg-[#D59A4C30] px-4 py-1 rounded-[40px]"
              >
                {showPersonalizationSection ? (
                  <>
                    <span className="mr-1">-</span>Collapse
                  </>
                ) : (
                  <>
                    <span className="mr-1">+</span>Expand
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
            These values override your default outreach settings until you update them here or on your{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavigateToSettings();
              }}
              className="text-[#D59A4C] hover:text-[#B58C54]"
            >
              settings page
            </a>.
          </p>

          {showPersonalizationSection && (
            <>
              {personalizationWarning && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 dark:bg-red-900/30 dark:border-red-600 flex items-start">
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5 dark:text-red-400"
                  />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    For best results with AI content generation, please fill out all personalization fields.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <input
                  type="text"
                  name="default_name"
                  value={personalizationData.default_name}
                  onChange={handlePersonalizationChange}
                  placeholder="Name"
                  className={`px-4 py-2 border ${
                    !personalizationData.default_name ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="text"
                  name="default_title"
                  value={personalizationData.default_title}
                  onChange={handlePersonalizationChange}
                  placeholder="Title"
                  className={`px-4 py-2 border ${
                    !personalizationData.default_title ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="text"
                  name="default_company"
                  value={personalizationData.default_company}
                  onChange={handlePersonalizationChange}
                  placeholder="Company"
                  className={`px-4 py-2 border ${
                    !personalizationData.default_company ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="email"
                  name="default_email"
                  value={personalizationData.default_email}
                  onChange={handlePersonalizationChange}
                  placeholder="Email"
                  className={`px-4 py-2 border ${
                    !personalizationData.default_email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="url"
                  name="default_website_url"
                  value={personalizationData.default_website_url}
                  onChange={handlePersonalizationChange}
                  placeholder="Website"
                  className={`px-4 py-2 border ${
                    !personalizationData.default_website_url ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  className="text-[#D59A4C] hover:text-[#B58C54] font-medium no-border"
                >
                  Advanced Options {isAdvancedOpen ? '▲' : '▼'}
                </button>
                <button
                  onClick={savePersonalizationData}
                  disabled={isSavingPersonalization || promptTooLong}
                  className="flex items-center px-4 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 transition-colors"
                >
                  {isSavingPersonalization ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>

              {isAdvancedOpen && (
                <div className="mt-4">
                  <hr className="border-gray-200 dark:border-gray-700 mb-3" />
                  <p className="text-sm text-gray-600 italic dark:text-gray-400 mb-3">
                    All you need are the settings above that are used with the app's default AI prompts to generate personalized content.<br/>
                    Below are optional advanced settings for further personalizing outreach content by creating your own prompt that overrides the app's defaults.
                  </p>
                  <div className="mb-2 flex items-center text-gray-700 dark:text-gray-200">
                    <span className="mr-2 font-medium">Custom Prompt (optional)</span>
                    <div className="relative">
                      <div
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border"
                        onMouseEnter={() => setShowPromptTooltip(true)}
                        onMouseLeave={() => setShowPromptTooltip(false)}
                        onClick={() => setShowPromptTooltip(v => !v)}
                      >
                        <SafeIcon icon={FiInfo} className="w-4 h-4 text-[#D59A4C]" />
                      </div>
                      {showPromptTooltip && (
                        <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                          Overrides the default AI prompts for generating personalized outreach content in Link Directory.
                        </div>
                      )}
                    </div>
                  </div>
                  <textarea
                    name="default_custom_prompt"
                    value={personalizationData.default_custom_prompt}
                    onChange={handlePersonalizationChange}
                    rows={5}
                    placeholder="e.g. Write a 150 word, persuasive outreach email introducing myself as [Your Name] from [Your Company] highlighting our eco-friendly dog toys to a blog owner. Begin with an engaging subject line and address the email to Hi Team."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  />
                  {promptTooLong && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">Custom prompt is limited to 225 words. Please shorten it to save.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Filters */}
        <LinkDirectoryFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          uniqueNiches={uniqueNiches}
          uniqueTypes={uniqueTypes}
          uniqueStates={uniqueStates}
          totalOpportunities={totalOpportunities}
          onExportCSV={exportToCSV}
        />

        {/* Results Table */}
        <LinkDirectoryTable
          opportunities={filteredOpportunities}
          user={user}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onStatusUpdate={loadOpportunities}
          onReportBrokenLink={handleReportBrokenLink}
          onFavorite={handleFavorite}
          onStatusChange={handleStatusChange}
          userActions={userActions}
          onPurchaseClick={onPurchaseClick}
          defaultValues={defaultValues}
          onSendEmail={sendEmailsViaWebhook}
        />

        {/* Report Broken Link Modal */}
        <ReportBrokenLinkModal
          isOpen={reportModalOpen}
          opportunity={reportedOpportunity}
          onClose={() => setReportModalOpen(false)}
          onSubmit={handleReportSubmit}
        />

        {/* Request Links Modal */}
        <RequestLinksModal
          isOpen={requestModalOpen}
          onClose={() => setRequestModalOpen(false)}
          onSubmit={handleRequestLinksSubmit}
        />

        {/* Request Logs Modal */}
        <RequestLogsModal
          isOpen={requestLogsOpen}
          onClose={() => setRequestLogsOpen(false)}
          logs={requestLogs}
        />
        {showBackToTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-[81px] right-[21px] p-3 bg-[#D59A4C] text-white rounded-full shadow-lg hover:bg-[#B58C54] transition-all z-50"
            aria-label="Back to top"
          >
            <img src="https://i.imghippo.com/files/GkAk1182MIE.webp" alt="K9 Links Dog" className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}

export default LinkDirectory
